import { Box, VStack, Flex, Grid } from "@chakra-ui/react";
import { CustomerRoutes } from "appRoutePaths";
import CallToActionBubble from "components/CallToActionBubble";
import { CSSProperties, FC } from "react";
import TopActiveListings from "./TopActiveListings";

import YourTopActiveListings from "./YourTopActiveListings";
import { useIdentity } from "context/auth";
import useTopActiveListings from "hooks/useTopActiveListings";
import useFeaturedAuctions from "hooks/useFeaturedAuctions";
import useAccountSummary from "hooks/useAccountSummary";
import DashboardInfoCard from "./DashboardInfoCard";
import ProcessingSubmissions from "./DashboardCards/ProcessingSubmissions";
import EbayActiveListings from "./DashboardCards/EbayActiveListings";
import MoneyOnTheWay from "./DashboardCards/MoneyOnTheWay";
import AvailableBalance from "./DashboardCards/AvailableBalance";
import ActiveListingsCount from "./DashboardCards/ActiveListingsCount";
import ActiveListingsBids from "./DashboardCards/ActiveListingsBids";
import DcsportsEbayListings from "./DashboardCards/DcsportsEbayListings";
import { pageRequestToUrlSearchParams } from "util/helpers";
import { DefaultPageRequest } from "util/getDefaultPageRequest";

const cardsAwaitingPaymentFilter = pageRequestToUrlSearchParams({
    ...DefaultPageRequest,
    filterBy: {
        status: ["AwaitingPayment"],
    },
});
const cardsActiveFilter = pageRequestToUrlSearchParams({
    ...DefaultPageRequest,
    filterBy: {
        status: ["Active"],
    },
});
const packagesProcessingFilter = pageRequestToUrlSearchParams({
    ...DefaultPageRequest,
    filterBy: {
        status: ["Processing"],
    },
});
const packagesProcessingLink = `${CustomerRoutes.packages.url}?${packagesProcessingFilter}`;

const cardsAwaitingPaymentLink = `${CustomerRoutes.cards.url}?${cardsAwaitingPaymentFilter}`;

const cardsActiveLink = `${CustomerRoutes.cards.url}?${cardsActiveFilter}`;

const CustomerDashboard: FC = () => {
    const { accounts } = useIdentity();
    const { data: topActiveListings } = useTopActiveListings(accounts[0]);
    const { data: featuredAuctions } = useFeaturedAuctions();
    const { data } = useAccountSummary(accounts[0]);

    return (
        <VStack spacing={4} fontFamily="body" pb={50} padding={4}>
            <Flex
                alignItems="stretch"
                width="100%"
                gap={2}
                minHeight="40vh"
                flexDir={{ base: "column", lg: "row" }}
            >
                <VStack flex={1} spacing={2}>
                    <DashboardInfoCard
                        flex={2}
                        minHeight={{ base: "20vh", md: "24vh" }}
                        _hover={{ outline: "4px solid var(--dcs-blue)" }}
                        style={{ "container-type": "size" } as CSSProperties}
                        as="button"
                        onClick={() =>
                            (window.location.href =
                                CustomerRoutes.requestPayout.url)
                        }
                    >
                        <AvailableBalance />
                    </DashboardInfoCard>

                    <DashboardInfoCard
                        justifyContent="space-between"
                        alignItems="left"
                        minHeight="15vh"
                        style={{ "container-type": "size" } as CSSProperties}
                        flex-={1}
                        _hover={{ outline: "2px solid var(--dcs-blue)" }}
                        as="button"
                        onClick={() =>
                            (window.location.href = cardsAwaitingPaymentLink)
                        }
                    >
                        <MoneyOnTheWay />
                    </DashboardInfoCard>
                </VStack>
                <Grid
                    gap={2}
                    flex={1}
                    templateColumns={{ base: "1", md: "repeat(2, 1fr)" }}
                >
                    <DashboardInfoCard
                        minHeight="15vh"
                        justifyContent="space-between"
                        alignItems="left"
                        style={{ "container-type": "size" } as CSSProperties}
                        _hover={{ outline: "2px solid var(--dcs-blue)" }}
                        as="button"
                        onClick={() =>
                            (window.location.href = CustomerRoutes.cards.url)
                        }
                    >
                        <ActiveListingsCount />
                    </DashboardInfoCard>
                    <DashboardInfoCard
                        justifyContent="space-between"
                        alignItems="left"
                        _hover={{ outline: "2px solid var(--dcs-blue)" }}
                        as="button"
                        onClick={() => (window.location.href = cardsActiveLink)}
                    >
                        <ActiveListingsBids />
                    </DashboardInfoCard>

                    <DashboardInfoCard
                        flex="1 1 50%"
                        justifyContent="space-between"
                        alignItems="left"
                        _hover={{ outline: "2px solid var(--dcs-blue)" }}
                        as="button"
                        onClick={() =>
                            (window.location.href = packagesProcessingLink)
                        }
                    >
                        <ProcessingSubmissions />
                    </DashboardInfoCard>
                    {topActiveListings?.data &&
                    topActiveListings.data.length > 0 &&
                    data?.activeListingsEbayLink ? (
                        <DashboardInfoCard
                            flex="1 1 50%"
                            as="button"
                            _hover={{
                                outline: "2px solid var(--dcs-blue)",
                            }}
                            onClick={() =>
                                window.open(
                                    data.activeListingsEbayLink,
                                    "_blank",
                                )
                            }
                        >
                            <EbayActiveListings />
                        </DashboardInfoCard>
                    ) : (
                        <DashboardInfoCard
                            flex="1 1 50%"
                            as="button"
                            _hover={{
                                outline: "2px solid var(--dcs-blue)",
                            }}
                            onClick={() =>
                                window.open(
                                    "https://www.ebay.com/str/uscgzachssportscards",
                                    "_blank",
                                )
                            }
                        >
                            <DcsportsEbayListings />
                        </DashboardInfoCard>
                    )}
                </Grid>
            </Flex>

            <Flex
                width="100%"
                alignItems="stretch"
                flexDir={{ base: "column", sm: "row" }}
                gap={4}
            >
                {topActiveListings?.data &&
                    topActiveListings.data.length > 0 && (
                        <Box overflow="scroll" flex={1}>
                            <YourTopActiveListings cards={topActiveListings} />
                        </Box>
                    )}
                {topActiveListings?.data &&
                    topActiveListings.data.length < 5 && (
                        <Box overflow="scroll" flex={1}>
                            <TopActiveListings cards={featuredAuctions} />
                        </Box>
                    )}
            </Flex>

            <CallToActionBubble
                message="Got Questions? We got Answers! Reach out any time."
                buttonText="Contact Us"
                buttonLink={CustomerRoutes.support.url}
            />
        </VStack>
    );
};

export default CustomerDashboard;
